import { Link } from "react-router-dom";
import { Facebook, FlaskConical, Instagram, Linkedin, MapPin, MessageCircle, Phone, Mail, Youtube } from "lucide-react";
import { PRODUCT_LINKS, SITE } from "@/lib/site";

const SOCIALS = [
  { icon: Facebook, label: "Facebook" },
  { icon: Instagram, label: "Instagram" },
  { icon: Linkedin, label: "LinkedIn" },
  { icon: Youtube, label: "YouTube" },
];

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Products" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact Us" },
  { to: "/admin", label: "Admin" },
];

export default function Footer() {
  return (
    <footer className="bg-navy text-white print:hidden" data-testid="footer">
      <div className="mx-auto max-w-7xl px-6 pb-10 pt-16 lg:pt-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-3">
              <img src="/img/ksc/logo.jpg" alt="KSC logo" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
              <span className="leading-none">
                <span className="block font-display text-lg font-extrabold tracking-tight">
                  KRITHISH <span className="text-rose-400">SCIENTIFIC COMPANY</span>
                </span>
                <span className="mt-1 block font-mono text-[9px] uppercase tracking-[0.3em] text-white/50">
                  Precision in Every Scale
                </span>
              </span>
            </Link>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/60">
              Krithish Scientific Company — manufacturer and leading supplier of laboratory, research, educational and
              medical instruments for 15+ years. Chemicals &amp; glass wares supplied; repairing &amp; servicing of all
              scientific, industrial &amp; lab equipments undertaken.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href="#"
                  aria-label={s.label}
                  onClick={(e) => e.preventDefault()}
                  className="rounded-full border border-white/15 p-2.5 text-white/70 transition hover:border-sky-400 hover:bg-safety hover:text-white"
                >
                  <s.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div className="lg:col-span-2">
            <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-rose-300">Quick Links</h3>
            <ul className="mt-5 space-y-3">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="text-sm text-white/60 transition hover:text-rose-300">
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div className="lg:col-span-3">
            <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-rose-300">Our Products</h3>
            <ul className="mt-5 space-y-3">
              {PRODUCT_LINKS.map((p) => (
                <li key={p.slug}>
                  <a href={`/products#${p.slug}`} className="text-sm text-white/60 transition hover:text-rose-300">
                    {p.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + map */}
          <div className="lg:col-span-3">
            <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-rose-300">Contact</h3>
            <ul className="mt-5 space-y-3 text-sm text-white/60">
              <li className="flex gap-2.5">
                <MapPin size={15} className="mt-0.5 shrink-0 text-rose-400" />
                <span>{SITE.addressLines.join(", ")}</span>
              </li>
              <li className="flex gap-2.5">
                <Phone size={15} className="mt-0.5 shrink-0 text-rose-400" />
                <a href={SITE.phoneHref} className="font-mono transition hover:text-rose-300">{SITE.phoneDisplay}</a>
              </li>
              <li className="flex gap-2.5">
                <Mail size={15} className="mt-0.5 shrink-0 text-rose-400" />
                <a href={`mailto:${SITE.email}`} className="transition hover:text-rose-300">{SITE.email}</a>
              </li>
              <li className="flex gap-2.5">
                <MessageCircle size={15} className="mt-0.5 shrink-0 text-rose-400" />
                <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" className="transition hover:text-rose-300">
                  WhatsApp Chat
                </a>
              </li>
            </ul>
            <iframe
              title="Company location map"
              src={SITE.mapEmbed}
              loading="lazy"
              className="mt-5 h-36 w-full rounded-lg border border-white/10 grayscale-[0.4]"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 sm:flex-row">
          <p className="text-xs text-white/40">© {new Date().getFullYear()} {SITE.name}. All rights reserved.</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
            GSTIN 33AINPR0146B1ZM · Chennai · Est. 2006
          </p>
        </div>
      </div>
    </footer>
  );
}
